import { useState } from 'react';
import { X, FileDown, ImageIcon } from 'lucide-react';
import { T } from '../theme';
import { generateExpensesPdf } from '../utils/pdfExport';

export default function PdfExportModal({
  expenses,
  reportName,
  ownerName = null,
  dateRange = null,
  defaultCurrency = 'USD',
  onClose,
}) {
  const [includeImages, setIncludeImages] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(null); // { done, total }
  const [error, setError] = useState('');

  const imageCount = expenses.filter(e => e.imagePreview).length;

  const handleGenerate = async () => {
    setGenerating(true);
    setError('');
    setProgress(null);
    try {
      await generateExpensesPdf(expenses, {
        reportName,
        ownerName,
        dateRange,
        defaultCurrency,
        includeImages,
        onProgress: includeImages && imageCount > 0
          ? (done, total) => setProgress({ done, total })
          : null,
      });
      onClose();
    } catch (err) {
      console.error('PDF generation failed:', err);
      setError('Failed to generate PDF. Please try again.');
    } finally {
      setGenerating(false);
      setProgress(null);
    }
  };

  const overlay = {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.6)',
    zIndex: 1000,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 16,
  };
  const box = {
    background: T.navy, borderRadius: 16, padding: 28,
    width: '100%', maxWidth: 380,
    color: '#fff', position: 'relative',
  };

  return (
    <div style={overlay} onClick={e => { e.stopPropagation(); e.target === e.currentTarget && onClose(); }}>
      <div style={box}>
        <button
          onClick={onClose}
          style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.4)' }}
        >
          <X size={18} />
        </button>

        {/* Title */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
          <div style={{ background: T.tealDeep, borderRadius: 10, padding: 8 }}>
            <FileDown size={18} color={T.tealBright} />
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>Download PDF</div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', marginTop: 2 }}>
              {reportName} · {expenses.length} expense{expenses.length !== 1 ? 's' : ''}
            </div>
          </div>
        </div>

        {/* Include images toggle */}
        <div
          onClick={() => imageCount > 0 && setIncludeImages(v => !v)}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            background: 'rgba(255,255,255,0.06)',
            border: `1.5px solid ${includeImages ? T.tealBright : 'rgba(255,255,255,0.12)'}`,
            borderRadius: 12, padding: '12px 14px',
            cursor: imageCount > 0 ? 'pointer' : 'default',
            marginBottom: 20,
            opacity: imageCount > 0 ? 1 : 0.4,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <ImageIcon size={16} color={includeImages ? T.tealBright : 'rgba(255,255,255,0.4)'} />
            <div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>Include receipt images</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>
                {imageCount === 0 ? 'No images in this report' : `${imageCount} image${imageCount !== 1 ? 's' : ''} will be appended`}
              </div>
            </div>
          </div>
          {/* Toggle pill */}
          <div style={{
            width: 38, height: 22, borderRadius: 11, flexShrink: 0,
            background: includeImages ? T.tealBright : 'rgba(255,255,255,0.15)',
            position: 'relative', transition: 'background 0.15s',
          }}>
            <div style={{
              position: 'absolute', top: 3,
              left: includeImages ? 19 : 3,
              width: 16, height: 16, borderRadius: '50%',
              background: '#fff', transition: 'left 0.15s',
            }} />
          </div>
        </div>

        {/* Progress */}
        {progress && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 6 }}>
              Loading images… {progress.done} / {progress.total}
            </div>
            <div style={{ height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.1)' }}>
              <div style={{
                height: '100%', borderRadius: 2,
                background: T.gradTeal,
                width: `${(progress.done / progress.total) * 100}%`,
                transition: 'width 0.2s',
              }} />
            </div>
          </div>
        )}

        {error && (
          <div style={{ fontSize: 13, color: '#f87171', marginBottom: 14 }}>{error}</div>
        )}

        <button
          onClick={handleGenerate}
          disabled={generating}
          style={{
            width: '100%', padding: '13px', borderRadius: 12, border: 'none',
            background: T.gradTeal, color: '#fff',
            fontSize: 15, fontWeight: 700,
            cursor: generating ? 'not-allowed' : 'pointer',
            opacity: generating ? 0.7 : 1,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}
        >
          <FileDown size={16} />
          {generating ? (progress ? `Loading images…` : 'Generating…') : 'Download PDF'}
        </button>
      </div>
    </div>
  );
}
